import {
  auditEventTypeSchema,
  type AuditEventType,
} from "../../../models/audit/auditEvent";

/**
 * The groupings every event type is sorted into. Only `control`, `workpaper`
 * and `evidence` are offered as chips; `lifecycle` (create/rename/delete,
 * review and approvals) is low-volume enough not to earn one, and still
 * belongs to the record and stays reachable under "All".
 */
export type AuditCategoryId =
  | "control"
  | "workpaper"
  | "evidence"
  | "lifecycle";

/**
 * Every event type belongs to exactly one category. The three chips name
 * *places* rather than actions — the control's own configuration, the
 * work-paper table, and the evidence supporting it — so each holds both the
 * inputs a generation run consumes and the edits an auditor makes to its
 * output. Keyed as a `Record<AuditEventType, ...>` (not a lookup with a
 * fallback) so a new backend event type fails `tsc` here rather than silently
 * disappearing from every filtered view.
 */
const CATEGORY_BY_EVENT_TYPE: Record<AuditEventType, AuditCategoryId> = {
  control_config_edited: "control",
  template_changed: "control",
  test_created: "lifecycle",
  session_renamed: "lifecycle",
  session_deleted: "lifecycle",
  submitted_for_review: "lifecycle",
  approval_requested: "lifecycle",
  approval_approved: "lifecycle",
  approval_rejected: "lifecycle",
  // Workspace membership, not a control or work paper: kept out of the chips.
  member_removed: "lifecycle",
  // Generation and reruns change the work-paper table's contents, so they
  // answer "what changed here" alongside the manual edits below. The Run Log
  // popover remains the surface for execution status itself.
  test_run: "workpaper",
  sample_rerun: "workpaper",
  cell_rerun: "workpaper",
  column_rerun: "workpaper",
  // Rematching changes which documents back each sample — an evidence change.
  documents_rematched: "evidence",
  manual_override: "workpaper",
  support_data_cell_edited: "workpaper",
  // The walkthrough evidences a cell in the work-paper table, so its edits belong
  // with the cell's other edits rather than in a category of their own.
  walkthrough_step_edited: "workpaper",
  walkthrough_verdict_edited: "workpaper",
  walkthrough_citation_imported: "workpaper",
  reperformance_step_added: "workpaper",
  reperformance_step_edited: "workpaper",
  reperformance_step_deleted: "workpaper",
  sample_row_added: "workpaper",
  sample_row_edited: "workpaper",
  sample_row_deleted: "workpaper",
  // The sample population is work-paper content, not evidence — this carries a
  // `sample` payload, the same family as the individual row events above.
  sample_upload_confirmed: "workpaper",
  result_locked: "workpaper",
  result_unlocked: "workpaper",
  result_deleted: "workpaper",
  // Citations annotate work-paper cells and trail steps — they belong with
  // the work they evidence (as walkthrough_citation_imported always has),
  // not under Documents, which holds file-level events only.
  citation_added: "workpaper",
  citation_updated: "workpaper",
  citation_deleted: "workpaper",
  citation_marked_key: "workpaper",
  evidence_added: "evidence",
  evidence_removed: "evidence",
};

/** The chips the drawer offers, in order. Excludes `lifecycle` by design. */
export const AUDIT_CATEGORIES: { id: AuditCategoryId; label: string }[] = [
  { id: "control", label: "Control" },
  { id: "workpaper", label: "Workpaper" },
  { id: "evidence", label: "Documents" },
];

/**
 * The concrete event types to send as the endpoint's repeated `event_types`
 * param. The category vocabulary is a client concern, so the mapping is
 * resolved here and the API only ever sees event types.
 */
export function eventTypesForCategory(
  category: AuditCategoryId,
): AuditEventType[] {
  return auditEventTypeSchema.options.filter(
    (eventType) => CATEGORY_BY_EVENT_TYPE[eventType] === category,
  );
}

export function categoryLabel(category: AuditCategoryId): string {
  return (
    AUDIT_CATEGORIES.find((category_) => category_.id === category)?.label ?? ""
  );
}
